/**
 * Certification Service
 * 
 * Handles all certification-related API calls for EX7 (Process Owner Certification)
 * Supports both Admin and Process Owner views
 */

import { apiRequest } from './apiClient';
import { SyncfusionGridParams, SyncfusionSearchParam } from './types';

// ========================================
// Types & Interfaces
// ========================================

/**
 * Fiscal Quarter (1-4)
 */
export type Quarter = 1 | 2 | 3 | 4;

/**
 * Significant Change status for checklist items
 */
export type SignificantChangeStatus = 'changeIdentified' | 'noChanges' | 'notApplicable';

/**
 * Comment type for certification cards
 */
export type CertificationCommentType = 
  | 'riskAssessment'
  | 'businessKnowledge'
  | 'flowcharts'
  | 'walkthroughs'
  | 'testSheet'
  | 'gaps'
  | 'checklist';

/**
 * Certification status for a process
 */
export type CertificationStatus = 'pending' | 'completed';

/**
 * Process Owner basic info for certification module
 */
export interface ProcessOwner {
  id: string;
  processOwnerName: string;
}

/**
 * Backend Process Owner response format from certification API
 */
interface BackendCertificationProcessOwner {
  Id: string;
  ProcessOwnerName: string;
}

/**
 * Certification Process Owners response
 */
export interface CertificationProcessOwnersResponse {
  result: BackendCertificationProcessOwner[];
  count: number;
}

/**
 * Backend response for process certification
 */
interface BackendProcessCertification {
  Id: string;
  ProcessId: string;
  ProcessLabel: string;
  ProcessOwnerId: string;
  QuarterOneCertificationDate: string;
  QuarterTwoCertificationDate: string;
  QuarterThreeCertificationDate: string;
  QuarterFourCertificationDate: string;
  CreatedAt: string;
}

/**
 * Process certification list response
 */
export interface ProcessCertificationListResponse {
  result: BackendProcessCertification[];
  count: number;
}

/**
 * Process with certification status (frontend format)
 */
export interface ProcessCertification {
  id: string; // Certification ID
  processId: string;
  processLabel: string;
  processOwnerId: string;
  q1SignOffDate: string | null;
  q2SignOffDate: string | null;
  q3SignOffDate: string | null;
  q4SignOffDate: string | null;
  createdAt: string;
}

/**
 * Process Owner with their certification data (for Admin Dashboard Tab 1)
 * Note: processes are now fetched separately per ProcessOwner component
 */
export interface ProcessOwnerCertificationGroup {
  processOwner: ProcessOwner;
  isExpanded: boolean;
}

/**
 * Attestation content configuration
 */
export interface AttestationContent {
  certificationQuarterId: string;
  processOwnerMessage: string;
  riskAssessmentTitle: string;
  riskAssessmentDescription: string;
  businessKnowledgeTitle: string;
  businessKnowledgeDescription: string;
  flowchartsTitle: string;
  flowchartsDescription: string;
  walkthroughsTitle: string;
  walkthroughsDescription: string;
  testSheetTitle: string;
  testSheetDescription: string;
  gapsTitle: string;
  gapsDescription: string;
  checklistTitle: string;
  checklistDescription: string;
}

/**
 * Checklist item (question + response) - Frontend format
 */
export interface ChecklistItem {
  checklistLineItemId: string;
  question: string;
  significantChange: SignificantChangeStatus | null;
  response: string;
  certificationQuarterId: string;
  isNew?: boolean; // For draft state tracking
  isDeleted?: boolean; // For draft state tracking
  isModified?: boolean; // For draft state tracking
}

/**
 * Backend response format for checklist item
 */
interface BackendChecklistItem {
  Id: string;
  Question: string;
  ChecklistKeyChange: string | null; // 'noChanges', 'changeIdentified', 'na'
  Response: string | null;
  CertificationQuarterId: string;
}

/**
 * Checklist items response from API
 */
export interface ChecklistItemsResponse {
  result: BackendChecklistItem[];
  count: number;
}

/**
 * Document/File in repository (Frontend format)
 */
export interface CertificationDocument {
  id: string;
  fileName: string;
  fileType: 'pdf' | 'doc' | 'docx' | 'xls' | 'xlsx' | 'ppt' | 'pptx' | 'png' | 'jpg' | 'other';
  fileSize: number; // in bytes
  createdAt: string;
}

/**
 * Backend response format for certification documents
 */
interface BackendCertificationDocument {
  Id: string;
  FileName: string;
  FileSize: number;
  CreatedAt: string;
}

/**
 * Documents list response from API
 */
export interface CertificationDocumentsResponse {
  result: BackendCertificationDocument[];
  count: number;
}

/**
 * Certification Quarter data (for Tab 2 - Certification Details)
 */
export interface CertificationQuarterData {
  certificationQuarterId: string;
  fiscalYear: number;
  quarter: Quarter;
  // Note: processOwner is NOT included here - use selectedProcessOwner from props/Redux instead
  attestation: AttestationContent;
  cycles: CycleWithProcesses[];
  comments: CertificationComment[];
}

/**
 * Cycle with nested processes
 */
export interface CycleWithProcesses {
  cycleId: string;
  cycleName: string;
  cycleCode: string;
  processes: ProcessChipData[];
}

/**
 * Flowchart file data
 */
export interface FlowchartFile {
  fileId: string;
  fileName: string;
  fileUrl: string;
}

/**
 * Walkthrough control data
 */
export interface WalkthroughFile {
  fileId: string;
  fileName: string;
}

/**
 * Gap data
 */
export interface GapItem {
  gapId: string;
  gapCode: string;
  gapName: string;
}

/**
 * Control activity data
 */
export interface ControlItem {
  controlId: string;
  controlCode: string;
}

/**
 * Process chip data for review cards
 */
export interface ProcessChipData {
  processId: string;
  processCode: string;
  processName: string;
  isCertified: boolean;
  // Additional data from API for review cards
  riskAssessmentId: string | null;
  flowcharts: FlowchartFile[];
  walkthroughs: WalkthroughFile[];
  testSheetId: string | null;
  testSheetName: string;
  gaps: GapItem[];
  controls: ControlItem[];
}

/**
 * File chip data for flowcharts, test sheets, etc.
 */
export interface FileChipData {
  fileId: string;
  fileName: string;
  processId: string;
  processName: string;
  isCertified: boolean;
  downloadUrl: string;
}

/**
 * Control chip data for walkthroughs
 */
export interface ControlChipData {
  controlId: string;
  controlCode: string;
  processId: string;
  processName: string;
  isCertified: boolean;
  files: FileChipData[];
}

/**
 * Gap chip data
 */
export interface GapChipData {
  gapId: string;
  gapCode: string;
  processId: string;
  processName: string;
  isCertified: boolean;
}

/**
 * Review card data structure
 */
export interface ReviewCardData {
  riskAssessment: ProcessChipData[];
  flowcharts: { processName: string; files: FileChipData[] }[];
  walkthroughs: { processName: string; controls: ControlChipData[] }[];
  testSheets: { processName: string; files: FileChipData[] }[];
  gaps: { processName: string; gaps: GapChipData[] }[];
}

/**
 * Certification comment
 */
export interface CertificationComment {
  commentId: string;
  message: string;
  type: CertificationCommentType;
  createdAt: string;
  createdBy: string;
}

// ========================================
// API Request/Response Types
// ========================================

export interface SendReminderRequest {
  processIds: string[];
  processOwnerId: string;
}

export interface UpdateAttestationRequest {
  certificationQuarterId: string;
  processOwnerMessage: string;
  riskAssessmentTitle: string;
  riskAssessmentDescription: string;
  businessKnowledgeTitle: string;
  businessKnowledgeDescription: string;
  flowchartsTitle: string;
  flowchardsDescription: string;
  walkthroughsTitle: string;
  walkthroughsDescription: string;
  testSheetTitle: string;
  testSheetDescription: string;
  gapsTitle: string;
  gapsDescription: string;
  checklistTitle: string;
  checklistDescription: string;
}

export interface AddCommentRequest {
  message: string;
  type: CertificationCommentType;
  certificationQuarterId: string;
}

export interface SubmitCertificationRequest {
  certificationQuarterId: string;
  processIds: string[];
}

export interface SubmitChecklistRequest {
  items: {
    // Admin: Only sends question and certificationQuarterId
    // PO: Sends all fields
    checklistLineItemId?: string;
    question: string;
    checklistKeyChange?: SignificantChangeStatus; // Optional for Admin
    response?: string; // Optional for Admin
    certificationQuarterId: string;
  }[];
}

export interface EmptyResponse {
  data: Record<string, never>;
  code: string;
  message: string;
}

// ========================================
// Backend Quarter Data Response Types
// ========================================

/**
 * Backend Process in Quarter Data
 */
export interface BackendQuarterControl {
  ControlId: string;
  ControlCode: string;
}

export interface BackendQuarterProcess {
  ProcessId: string;
  ProcessCode: string;
  ProcessLabel: string;
  RiskAssessmentId: string | null;
  Flowcharts: BackendQuarterFile[];
  Walkthroughs: BackendQuarterFile[];
  TestSheetId: string | null;
  TestSheetName: string;
  Gaps: BackendQuarterGap[];
  Controls: BackendQuarterControl[];
  IsCertified: boolean;
}

/**
 * Backend File in Quarter Data (for Flowcharts, TestSheets)
 */
export interface BackendQuarterFile {
  FileId: string;
  FileName: string;
  FileUrl: string;
}

/**
 * Backend Walkthrough in Quarter Data
 */
export interface BackendQuarterWalkthrough {
  WalkthroughId: string;
  ControlCode: string;
  ControlName: string;
}

/**
 * Backend Gap in Quarter Data
 */
export interface BackendQuarterGap {
  GapId: string;
  GapCode: string;
  GapName: string;
}

/**
 * Backend Comment in Quarter Data
 */
export interface BackendQuarterComment {
  CommentId: string;
  Message: string;
  Type: string;
  CreatedAt: string;
  CreatedBy: string;
}

/**
 * Backend Quarter Data Item (single item in result array)
 */
export interface BackendQuarterDataItem {
  Quarter: number;
  ProcessOwnerMessage: string;
  RiskAssessmentTitle: string;
  RiskAssessmentDescription: string;
  BusinessKnowledgeTitle: string;
  BusinessKnowledgeDescription: string;
  FlowchartsTitle: string;
  FlowchardsDescription: string; // Note: Backend typo
  WalkthroughsTitle: string;
  WalkthroughsDescription: string;
  TestSheetTitle: string;
  TestSheetDescription: string;
  GapsTitle: string;
  GapsDescription: string;
  ChecklistTitle: string;
  ChecklistDescription: string;
  Processes: BackendQuarterProcess[];
  Comments: BackendQuarterComment[];
  CreatedAt: string;
  Id: string;
}

/**
 * Backend Quarter Data Response
 */
export interface BackendQuarterDataResponse {
  result: BackendQuarterDataItem[];
  count: number;
}

// ========================================
// Helper Functions
// ========================================

/**
 * Get current fiscal quarter based on date
 */
export const getCurrentQuarter = (): Quarter => {
  const month = new Date().getMonth() + 1; // 1-12
  if (month >= 1 && month <= 3) return 1;
  if (month >= 4 && month <= 6) return 2;
  if (month >= 7 && month <= 9) return 3;
  return 4;
};

/**
 * Get current fiscal year
 */
export const getCurrentFiscalYear = (): number => {
  return new Date().getFullYear();
};

/**
 * Format date for display (MM/DD/YYYY)
 */
export const formatCertificationDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Get file type icon based on extension
 */
export const getFileTypeIcon = (fileType: CertificationDocument['fileType']): string => {
  const icons: Record<CertificationDocument['fileType'], string> = {
    pdf: 'file-pdf',
    doc: 'file-word',
    docx: 'file-word',
    xls: 'file-excel',
    xlsx: 'file-excel',
    ppt: 'file-ppt',
    pptx: 'file-ppt',
    png: 'file-image',
    jpg: 'file-image',
    other: 'file',
  };
  return icons[fileType] || 'file';
};

/**
 * Parse backend date to frontend format
 * Backend returns "-" for null dates
 */
const parseBackendDate = (dateStr: string): string | null => {
  if (!dateStr || dateStr === '-') return null;
  return dateStr;
};

/**
 * Parse backend process certification to frontend format
 */
const parseBackendProcessCertification = (backend: BackendProcessCertification): ProcessCertification => ({
  id: backend.Id,
  processId: backend.ProcessId,
  processLabel: backend.ProcessLabel,
  processOwnerId: backend.ProcessOwnerId,
  q1SignOffDate: parseBackendDate(backend.QuarterOneCertificationDate),
  q2SignOffDate: parseBackendDate(backend.QuarterTwoCertificationDate),
  q3SignOffDate: parseBackendDate(backend.QuarterThreeCertificationDate),
  q4SignOffDate: parseBackendDate(backend.QuarterFourCertificationDate),
  createdAt: backend.CreatedAt,
});

/**
 * Fetch process certifications for a specific process owner
 * 
 * API: POST /certifications/list/index-grid/{annualReportId}/{processOwnerId}
 * 
 * @param annualReportId - Annual report ID
 * @param processOwnerId - Process owner ID
 * @param search - Optional search params
 */
export const getProcessCertificationsForOwner = async (
  annualReportId: string,
  processOwnerId: string,
  search?: SyncfusionSearchParam[]
): Promise<ProcessCertification[]> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: 0,
      take: 1000,
      requiresCounts: true,
      sorted: [{ name: 'ProcessLabel', direction: 'ascending' as const }],
      ...(search && { search }),
    };
    
    const response = await apiRequest<ProcessCertificationListResponse, SyncfusionGridParams>(
      'POST',
      `/certifications/list/index-grid/${annualReportId}/${processOwnerId}`,
      requestBody,
      true // Authentication required
    );
    
    // Map backend response to frontend format
    return response.result.map(parseBackendProcessCertification);
  } catch (error) {
    console.error('Error fetching process certifications for owner:', error);
    throw error;
  }
};

/**
 * Fetch process owners list for certification module
 * 
 * API: POST /certifications/process-owners/index-grid/{companyId}
 * Note: For PO users, this only returns self
 * 
 * @param companyId - Company ID from user's info
 */
export const getProcessOwners = async (
  companyId: string
): Promise<ProcessOwner[]> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: 0,
      take: 1000,
      requiresCounts: true,
      sorted: [{ name: 'ProcessOwnerName', direction: 'ascending' as const }],
    };
    const response = await apiRequest<CertificationProcessOwnersResponse, typeof requestBody>(
      'POST',
      `/certifications/process-owners/index-grid/${companyId}`,
      requestBody,
      true // Authentication required
    );
    
    // Map backend response to frontend format
    return response.result.map((owner: BackendCertificationProcessOwner) => ({
      id: owner.Id,
      processOwnerName: owner.ProcessOwnerName,
    }));
  } catch (error) {
    console.error('Error fetching process owners:', error);
    throw error;
  }
};

/**
 * Parse backend quarter process to frontend format
 */
const parseBackendQuarterProcess = (backend: BackendQuarterProcess): ProcessChipData => ({
  processId: backend.ProcessId,
  processCode: backend.ProcessCode,
  processName: backend.ProcessLabel,
  isCertified: backend.IsCertified,
  riskAssessmentId: backend.RiskAssessmentId,
  flowcharts: (backend.Flowcharts || []).map(f => ({
    fileId: f.FileId,
    fileName: f.FileName,
    fileUrl: f.FileUrl,
  })),
  walkthroughs: (backend.Walkthroughs || []).map(w => ({
    fileId: w.FileId,
    fileName: w.FileName,
  })),
  testSheetId: backend.TestSheetId,
  testSheetName: backend.TestSheetName || '',
  gaps: (backend.Gaps || []).map(g => ({
    gapId: g.GapId,
    gapCode: g.GapCode,
    gapName: g.GapName,
  })),
  controls: (backend.Controls || []).map(c => ({
    controlId: c.ControlId,
    controlCode: c.ControlCode,
  })),
});

/**
 * Parse backend quarter comment to frontend format
 */
const parseBackendQuarterComment = (backend: BackendQuarterComment): CertificationComment => ({
  commentId: backend.CommentId,
  message: backend.Message,
  type: backend.Type as CertificationCommentType,
  createdAt: backend.CreatedAt,
  createdBy: backend.CreatedBy,
});

/**
 * Parse backend quarter data item to frontend CertificationQuarterData format
 */
const parseBackendQuarterData = (
  backend: BackendQuarterDataItem
): CertificationQuarterData => {
  const processes = backend.Processes.map(parseBackendQuarterProcess);
  
  // Group processes by cycle code prefix (e.g., A01 -> A, B02 -> B)
  // Note: API doesn't return cycles, so we derive them from process codes
  const cycleGroups: Record<string, ProcessChipData[]> = {};
  
  processes.forEach(proc => {
    // Extract cycle code from process code (first letter)
    const cycleCode = proc.processCode.charAt(0);
    if (!cycleGroups[cycleCode]) {
      cycleGroups[cycleCode] = [];
    }
    cycleGroups[cycleCode].push(proc);
  });
  
  // Convert to CycleWithProcesses array
  // Use cycle code format (e.g., A00, B00) as cycleName since API doesn't return cycle names
  const cycles: CycleWithProcesses[] = Object.entries(cycleGroups).map(([code, procs]) => ({
    cycleId: `cycle-${code}`,
    cycleName: `${code}00`,
    cycleCode: `${code}00`,
    processes: procs,
  }));
  
  return {
    certificationQuarterId: backend.Id,
    fiscalYear: getCurrentFiscalYear(),
    quarter: backend.Quarter as Quarter,
    attestation: {
      certificationQuarterId: backend.Id,
      processOwnerMessage: backend.ProcessOwnerMessage,
      riskAssessmentTitle: backend.RiskAssessmentTitle,
      riskAssessmentDescription: backend.RiskAssessmentDescription,
      businessKnowledgeTitle: backend.BusinessKnowledgeTitle,
      businessKnowledgeDescription: backend.BusinessKnowledgeDescription,
      flowchartsTitle: backend.FlowchartsTitle,
      flowchartsDescription: backend.FlowchardsDescription, // Note: Backend typo
      walkthroughsTitle: backend.WalkthroughsTitle,
      walkthroughsDescription: backend.WalkthroughsDescription,
      testSheetTitle: backend.TestSheetTitle,
      testSheetDescription: backend.TestSheetDescription,
      gapsTitle: backend.GapsTitle,
      gapsDescription: backend.GapsDescription,
      checklistTitle: backend.ChecklistTitle,
      checklistDescription: backend.ChecklistDescription,
    },
    cycles,
    comments: backend.Comments.map(parseBackendQuarterComment),
  };
};

/**
 * Fetch certification quarter data (Admin Tab 2 / PO Tab 1)
 * 
 * API: POST /certifications/quarter/index-grid/{annualReportId}/{processOwnerId}/{quarter}
 * Note: API returns array but we only need first element
 * 
 * @returns CertificationQuarterData or null if no data/error
 */
export const getCertificationQuarterData = async (
  annualReportId: string,
  processOwnerId: string,
  quarter: Quarter
): Promise<CertificationQuarterData | null> => {
  try {
    const response = await apiRequest<BackendQuarterDataResponse, SyncfusionGridParams>(
      'POST',
      `/certifications/quarter/index-grid/${annualReportId}/${processOwnerId}/${quarter}`,
      { skip: 0, take: 10, requiresCounts: true },
      true // Authentication required
    );
    
    // API returns array but we only need first element (as per user instruction)
    if (response.result && response.result.length > 0) {
      const firstItem = response.result[0];
      return parseBackendQuarterData(firstItem);
    }
    
    // No data returned - return null for empty state
    console.warn('No quarter data returned from API');
    return null;
  } catch (error) {
    console.error('Error fetching certification quarter data:', error);
    // Re-throw error for component to handle
    throw error;
  }
};

/**
 * Get file extension and determine file type
 */
const getFileTypeFromName = (fileName: string): CertificationDocument['fileType'] => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const typeMap: Record<string, CertificationDocument['fileType']> = {
    pdf: 'pdf',
    doc: 'doc',
    docx: 'docx',
    xls: 'xls',
    xlsx: 'xlsx',
    ppt: 'ppt',
    pptx: 'pptx',
    png: 'png',
    jpg: 'jpg',
    jpeg: 'jpg',
  };
  return typeMap[ext] || 'other';
};

/**
 * Fetch documents for process owner (Admin Tab 3 / PO Tab 2)
 */
export const getCertificationDocuments = async (
  annualReportId: string,
  processOwnerId: string
): Promise<CertificationDocument[]> => {
  try {
    // console.log('📁 Fetching documents:', { annualReportId, processOwnerId });
    
    const response = await apiRequest<CertificationDocumentsResponse, SyncfusionGridParams>(
      'POST',
      `/certifications/downloads/index-grid/${annualReportId}/${processOwnerId}`,
      { skip: 0, take: 1000, requiresCounts: true, sorted: [{ name: 'FileName', direction: 'ascending' as const }] },
      true
    );
        
    // Map backend response to frontend format
    return response.result.map(doc => ({
      id: doc.Id,
      fileName: doc.FileName,
      fileType: getFileTypeFromName(doc.FileName),
      fileSize: doc.FileSize,
      createdAt: doc.CreatedAt,
    }));
  } catch (error) {
    console.error('Error fetching certification documents:', error);
    throw error;
  }
};

/**
 * Map backend checklist key change to frontend SignificantChangeStatus
 */
const mapChecklistKeyChange = (value: string | null): SignificantChangeStatus | null => {
  if (!value) return null;
  const normalized = value.toLowerCase();
  if (normalized === 'changeidentified' || normalized === 'change_identified') return 'changeIdentified';
  if (normalized === 'nochanges' || normalized === 'no_changes') return 'noChanges';
  if (normalized === 'na' || normalized === 'n/a' || normalized === 'notapplicable') return 'notApplicable';
  return null;
};

/**
 * Fetch checklist items (Admin Tab 4 / PO Tab 3)
 */
export const getChecklistItems = async (
  annualReportId: string,
  processOwnerId: string,
  quarter: Quarter,
  certificationQuarterId?: string
): Promise<ChecklistItem[]> => {
  try {
    // console.log('📋 Fetching checklist items:', { annualReportId, processOwnerId, quarter });
    const requestBody: SyncfusionGridParams = {
      skip: 0,
      take: 1000,
      requiresCounts: true,
      sorted: [{ name: 'Number', direction: 'ascending' as const }],
    };
    const response = await apiRequest<ChecklistItemsResponse, SyncfusionGridParams>(
      'POST',
      `/certifications/checklist/index-grid/${annualReportId}/${processOwnerId}/${quarter}`,
      requestBody,
      true
    );
    
    // Map backend response to frontend format
    return response.result.map(item => ({
      checklistLineItemId: item.Id,
      question: item.Question,
      significantChange: mapChecklistKeyChange(item.ChecklistKeyChange),
      response: item.Response || '',
      certificationQuarterId: item.CertificationQuarterId || certificationQuarterId || '',
    }));
  } catch (error) {
    console.error('Error fetching checklist items:', error);
    throw error;
  }
};

// Note: getProcessCertificationList removed - use getProcessCertificationsForOwner instead
// Both PO Overview Tab (Tab 4) and Admin Dashboard Tab (Tab 1) use the same API endpoint

// ========================================
// Mutation Functions
// ========================================

/**
 * Send certification reminder
 */
export const sendCertificationReminder = async (
  request: SendReminderRequest
): Promise<EmptyResponse> => {
  return apiRequest<EmptyResponse, SendReminderRequest>(
    'POST',
    '/certifications/remind',
    request
  );
};

/**
 * Update attestation content (Admin)
 */
export const updateAttestationContent = async (
  request: UpdateAttestationRequest
): Promise<EmptyResponse> => {
  return apiRequest<EmptyResponse, UpdateAttestationRequest>(
    'PUT',
    '/certifications/quarter',
    request
  );
};

/**
 * Add comment to certification quarter (PO)
 */
export const addCertificationComment = async (
  request: AddCommentRequest
): Promise<EmptyResponse> => {
  return apiRequest<EmptyResponse, AddCommentRequest>(
    'POST',
    '/certifications/quarter/comment',
    request
  );
};

/**
 * Delete certification comment
 */
export const deleteCertificationComment = async (
  commentId: string
): Promise<EmptyResponse> => {
  return apiRequest<EmptyResponse>(
    'DELETE',
    `/certifications/quarter/comment/${commentId}`
  );
};

/**
 * Submit certification (PO sign-off)
 */
export const submitCertification = async (
  request: SubmitCertificationRequest
): Promise<EmptyResponse> => {
  return apiRequest<EmptyResponse, SubmitCertificationRequest>(
    'POST',
    '/certifications/quarter/certify',
    request
  );
};

/**
 * Submit checklist (Admin publishes questions / PO saves responses)
 */
export const submitChecklist = async (
  request: SubmitChecklistRequest
): Promise<EmptyResponse> => {
  return apiRequest<EmptyResponse, SubmitChecklistRequest>(
    'POST',
    '/certifications/quarter/checklist',
    request
  );
};

/**
 * Delete a checklist item (Admin only)
 * Used when deleting an existing (submitted) question
 */
export const deleteChecklistItem = async (
  checklistLineItemId: string
): Promise<EmptyResponse> => {
  return apiRequest<EmptyResponse>(
    'DELETE',
    `/certifications/quarter/checklist/${checklistLineItemId}`
  );
};

// ========================================
// Certification Download Functions
// ========================================

/**
 * Presigned URL response for certification download
 */
export interface CertificationPresignedUrlResponse {
  data: {
    downloadUrl: string;
  };
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Get presigned URL for downloading certification PDF
 * 
 * API: GET /file-uploads/certification/presigned-url/{processOwnerId}/{processId}/{quarter}
 * 
 * @param processOwnerId - Process owner ID
 * @param processId - Process ID
 * @param quarter - Quarter number (1-4)
 * @returns Presigned URL response with download URL
 */
export const getCertificationPresignedUrl = async (
  processOwnerId: string,
  processId: string,
  quarter: Quarter
): Promise<CertificationPresignedUrlResponse> => {
  try {
    return await apiRequest<CertificationPresignedUrlResponse, never>(
      'GET',
      `/file-uploads/certification/presigned-url/${processOwnerId}/${processId}/${quarter}`,
      undefined,
      true // Authentication required
    );
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to get certification download URL',
    };
  }
};

/**
 * Download certification PDF file
 * Opens the presigned URL in a new tab to trigger download
 * 
 * @param processOwnerId - Process owner ID
 * @param processId - Process ID
 * @param quarter - Quarter number (1-4)
 */
export const downloadCertification = async (
  processOwnerId: string,
  processId: string,
  quarter: Quarter
): Promise<void> => {
  try {
    const response = await getCertificationPresignedUrl(processOwnerId, processId, quarter);
    
    if (!response.data?.downloadUrl) {
      throw new Error('No download URL returned');
    }
    
    // Open the presigned URL directly to trigger download
    const link = document.createElement('a');
    link.href = response.data.downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw error;
    }
    throw {
      code: 'UNEXPECTED_ERROR',
      message: error instanceof Error ? error.message : 'Failed to download certification',
    };
  }
};
